import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import Flex from '~/common/components/Flex';

import language from '../../../utils/language.json';
import ShowMoreButton from '../../buttons/ShowMoreButton';
import { StyledSmartAnswerText } from '../../styledComponents/styledComponents';

type DetailedAnswerProps = {
  detailedAnswer: string;
  isOpen?: boolean;
  paddingTop?: string;
  paddingBottom?: string;
};

/**
 * @typedef {object} DetailedAnswerProps
 * @property {string} detailedAnswer - The detailed answer to display.
 * @property {boolean} [isOpen=false] - Whether the detailed answer should be open by default.
 * @property {string} [paddingTop='1rem'] - The padding top of the component.
 *
 * @param {DetailedAnswerProps} props
 * @returns
 */
export default function DetailedAnswer({
  detailedAnswer,
  isOpen = false,
  paddingTop = '1rem',
  paddingBottom = '1rem',
}: Readonly<DetailedAnswerProps>) {
  const [showMore, setShowMore] = useState(isOpen);
  const [lineHeight, setLineHeight] = useState(0);
  const [textHeight, setTextHeight] = useState(0);

  const containerRef = useRef(null);
  const textRef = useRef(null);

  const toggleShowMoreButton = () => {
    setShowMore((prev) => !prev);
  };

  const hasEllipsis = useMemo(() => {
    return textHeight > lineHeight;
  }, [lineHeight, textHeight]);

  const setTextHeights = () => {
    if (!(textRef.current instanceof Element)) {
      return;
    }

    const lineHeight = Number.parseFloat(
      getComputedStyle(textRef.current).lineHeight,
    );
    const textHeight = textRef.current.clientHeight;
    setLineHeight(lineHeight);
    setTextHeight(textHeight);
  };

  const textHeightRef = useCallback((node) => {
    if (node !== null) {
      setTimeout(() => {
        textRef.current = node;
        setTextHeights();
      }, 0);
    }
  }, []);

  useEffect(() => {
    const containerNode = containerRef.current;

    const handleResize = () => {
      setTextHeights();
    };

    const resizeObserver = new ResizeObserver(handleResize);

    if (containerNode) {
      resizeObserver.observe(containerNode);
    }

    // Clean up the observer on component unmount
    return () => {
      if (containerNode) {
        resizeObserver.unobserve(containerNode);
      }

      resizeObserver.disconnect();
    };
  }, []);

  if (detailedAnswer === '') {
    return null;
  }

  return (
    <Flex
      ref={containerRef}
      width="100%"
      position="relative"
      overflow="hidden"
      paddingTop={paddingTop}
      paddingBottom={paddingBottom}
      direction={showMore ? 'column' : 'row'}
      alignItems={showMore ? 'flex-end' : 'center'}
    >
      <div
        style={{
          overflow: 'hidden',
          height: showMore ? 'auto' : lineHeight + 'px',
          margin: 0,
          padding: 0,
          width: '100%',
          flex: 1,
          cursor: 'pointer',
        }}
      >
        <StyledSmartAnswerText ref={textHeightRef}>
          {detailedAnswer}
        </StyledSmartAnswerText>
      </div>
      {hasEllipsis && (
        <ShowMoreButton
          showMoreText={`${language.ELLIPSIS}${language.SHOW_MORE}`}
          isExpanded={showMore}
          onClick={toggleShowMoreButton}
        />
      )}
    </Flex>
  );
}
